// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-04-23 20:54:49 +0200 (Wed, 23 Apr 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 151901 $
//  Purpose   : parts for jobs
// =============================================================================
// ---Third-party imports
import React from 'react'
import {
  Table,
  Grid,
  Loader,
  Input,
  Item,
  Menu,
  Icon,
  Segment,
  Button,
  Dropdown,
} from 'semantic-ui-react'
import {matchSorter} from 'match-sorter'
import clsx from 'clsx'

// ---Framework imports
import {get, size, values, has, sorted, uniq, flatten, mapItems, isString} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import {funcPart} from 'uix/parts'
import log from 'uix/logging'
import connect from 'uix/connect'
import {
  useSelector,
  usePartContext,
  useActions,
  useDebounce,
  useMemo,
  useApi,
  useState,
  useRouter,
  useEffect,
  usePersistent,
  usePersistentWithQueryOverride,
} from 'uix/hooks'
import {
  HeaderSettingsIcon,
  HeaderSegment,
  TableSegment,
  DDMenu,
  XMenu,
  DebugFeature,
  TranslatedTable,
} from 'uix/components'
import {DomainLabel} from 'uix/domain/label'

// ---Local imports
import {loadJobSpec, startJob} from './actions'
import styles from './styles.scss'
import {Job, JobTable, DeleteJobButton, JobMenuButton, cleanName, DEFAULT_DAYS} from './components'
import {useJobs, useActiveJobRegistration} from './hooks'
import {jobsIcon} from './utils'

// ---Exports

const useParamToggle = () => {
  let [showParamDocs, setShowParamDocs] = usePersistent('showParamDocs', false)
  let showParamToggle = (
    <XMenu.Toggle
      icon="help"
      title="Show parameter docs"
      set={setShowParamDocs}
      value={showParamDocs}
    />
  )
  return {showParamDocs, showParamToggle}
}
export const JobSearch = ({i18n}) => {
  let {specLoaded, jobSpec} = useJobs()

  let [showDocs, setShowDocs] = usePersistent('showDocs', true)
  let {showParamDocs, showParamToggle} = useParamToggle()

  let [searchTerm, setSearchTerm] = usePersistentWithQueryOverride({
    name: 'filter',
    initialValue: '',
    replace: true,
  })

  let {jobs, groups} = useMemo(() => {
    let jobs = values(jobSpec)
    let groups = uniq(flatten(jobs.map(j => j.groups)))
    if (searchTerm.length > 2) {
      let keys = ['name', 'groups']
      let _searchTerm = searchTerm
      if (searchTerm.startsWith('g:')) {
        _searchTerm = searchTerm.slice(2)
        keys = ['groups']
      }
      jobs = matchSorter(jobs, _searchTerm, {
        keys,
        threshold: matchSorter.rankings.CONTAINS,
      })
    } else {
      jobs = jobs.slice(0, 20)
    }
    return {jobs, groups}
  }, [jobSpec, searchTerm])

  if (!specLoaded) return <Loader active />

  let ddJobGroups = groups.map(g => (
    <Dropdown.Item key={g} onClick={() => setSearchTerm(`g:${g}`)}>
      {i18n.get(`jobGroup_${g}`)}
    </Dropdown.Item>
  ))

  let menu = (
    <XMenu>
      <XMenu.Dropdown scrolling text={`${jobs.length}/${size(jobSpec)}`}>
        <Dropdown.Menu>{ddJobGroups}</Dropdown.Menu>
      </XMenu.Dropdown>
      <XMenu.Input
        icon="search"
        placeholder="search"
        value={searchTerm}
        debounce={1000}
        onValueChange={setSearchTerm}
      />
      <XMenu.Toggle icon="info" title="Show job docs" set={setShowDocs} value={showDocs} />
      {showParamToggle}
    </XMenu>
  )
  //~ let subheader = `${size(jobs)}/${size(jobSpec)}`
  return (
    <HeaderSegment major header={'Jobs'} menu={menu} icon={jobsIcon('play')}>
      <Item.Group style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'space-arround'}}>
        {jobs.map(e => (
          <Job key={e.name} showDocs={showDocs} showParamDocs={showParamDocs} showName {...e} />
        ))}
      </Item.Group>
    </HeaderSegment>
  )
}

export const TestJobs = JobSearch

export const JobList = funcPart(({request, partcfg}) => {
  let i18n = useSelector(Sel.i18n)
  let latestInfo = useSelector(root => root.data.job.info)

  let [names, setNames] = usePersistent('jobNames', '')

  if (partcfg.filter_names) {
    names = partcfg.filter_names
  }

  let {data, error, loading} = useApi(
    'filter/getJobs',
    {days: request.query.days || DEFAULT_DAYS, max: 5000, names},
    {partReloadable: true, autoreset: false}
  )

  let jobs = data?.jobs || []
  let listOptions = data?.listOptions || {}

  let {embeddedInfo} = partcfg
  let ojid = request.id && String(request.id).startsWith('j') ? request.id.slice(1) : null

  if (embeddedInfo && ojid) {
    return <InfoPane ojid={ojid} showBackToList />
  } else {
    let {jobButton} = partcfg

    // XXX: maybe this deserves a memoization strategy
    let available = jobs.filter(j => latestInfo[j.ojid])
    if (available.length > 0) {
      jobs = jobs.map(j => get(latestInfo, j.ojid, j))
    }

    // add newer jobs
    let max_ojid = Math.max(...jobs.map(j => j.ojid))
    let newer = mapItems(latestInfo, (ojid, j) => j, ojid => ojid > max_ojid)
    if (newer.length > 0) {
      jobs = [...newer, ...jobs]
    }

    let mbutton = null
    if (jobButton) {
      jobButton = isString(jobButton) ? [jobButton] : jobButton
      mbutton = jobButton.map(jobName => <JobMenuButton key={jobName} confirm name={jobName} />)
    }

    let menu = (
      <XMenu>
        {mbutton}
        {!partcfg.filter_names && (
          <XMenu.Input icon="filter" value={names} onValueChange={setNames} debounce={1000} />
        )}
      </XMenu>
    )

    return (
      <TableSegment
        header={i18n.get('jobsHeader')}
        subheader={i18n.get('jobsSubHeader', {count: jobs.length, days: listOptions.days})}
        icon={jobsIcon('eye')}
        menu={menu}
        portal="JobMenuPortal"
        loading={loading}
      >
        <JobTable
          flex
          selectable
          filterable
          jobs={jobs}
          listOptions={listOptions}
          linkTarget={embeddedInfo ? null : 'job/id'}
        />
      </TableSegment>
    )
  }
})

const InfoPane = ({ojid, jobInfo, showBackToList}) => {
  let {data, error} = useApi('filter/getJobInfo', {id: `j${ojid}`}, {condition: !jobInfo && ojid})
  if (data) {
    jobInfo = data.jobInfo
  }
  useActiveJobRegistration(jobInfo || {})

  let {showParamDocs, showParamToggle} = useParamToggle()

  let i18n = useSelector(Sel.i18n)
  let apiurl = useSelector(Sel.apiurl)
  let {jobSpec, specLoaded, stopJob, terminateJob, restartJob} = useJobs()
  let ojid2info = useSelector(root => root.data.job.info)
  let [viewMode, setViewMode] = useState(null)

  let content = null
  let menu = null
  let subheader = <Loader inline active size="tiny" />
  let jobName = ''

  if (!specLoaded) {
    content = <Loader active />
  } else if (!jobInfo || !jobInfo.name) {
    //
  } else {
    jobName = jobInfo.name
    let spec = jobSpec[jobName]

    let {state} = ojid2info[jobInfo.ojid] || jobInfo
    let onDelete = () => {}

    let isExternal = jobInfo.history.some(e => e[1] === 'STARTING_EXTERNAL')

    menu = (
      <XMenu>
        <XMenu.Item
          icon="redo"
          content="Restart"
          onClick={() => restartJob(jobInfo.ojid)}
          confirm={`Restart Job ${jobInfo.ojid}?`}
        />
        {spec.stoppable && (
          <XMenu.Item
            icon="stop"
            content="Stop"
            title="Send stop signal"
            disabled={state != 'RUNNING'}
            onClick={() => stopJob(jobInfo.ojid)}
            confirm={`Stop Job ${jobInfo.ojid}?`}
          />
        )}
        <DebugFeature>
          <XMenu.Item
            icon={['stop', {name: 'lightning', color: 'red'}]}
            content="Terminate"
            title="Inject exception into running python byte code"
            disabled={state != 'RUNNING' && state != 'STOPPING'}
            onClick={() => stopJob(jobInfo.ojid, true)}
            confirm={`Terminate Job ${jobInfo.ojid}?`}
          />
        </DebugFeature>
        {isExternal && (
          <XMenu.LinkItem
            icon={{name: 'binoculars', color: 'blue'}}
            path={`${apiurl}/log/jobnode/${jobInfo.ojid}`}
            title="Show log of jobnode"
          />
        )}
        <DeleteJobButton jobs={[jobInfo.ojid]} onDelete={onDelete} />
        {viewMode == 'options' && showParamToggle}
      </XMenu>
    )
    // XXX: we should reuse jobInfo
    content = (
      <Job
        ojid={jobInfo.ojid}
        resolveDependencies
        showBackToList={showBackToList}
        showParamDocs={showParamDocs}
        showDocs={showParamDocs}
        onViewModeChange={setViewMode}
        {...spec}
      />
    )
    subheader = i18n.get('jobSubHeader', jobInfo)
  }

  return (
    <HeaderSegment
      major
      header={i18n.get('jobHeader', {name: cleanName(jobName)})}
      subheader={subheader}
      icon="cog"
      menu={menu}
      portal="JobInfoMenuPortal"
      flex
      overflowInner
    >
      <Item.Group divided>{content}</Item.Group>
    </HeaderSegment>
  )
}

export const JobInfo = ({request}) => {
  return <InfoPane ojid={String(request.id).replace('j', '')} />
}

export const SchedulerPanel = () => {
  let {data, error, loading, reload} = useApi('filter/getSchedulerInfo', {}, {autoreset: false})
  let callApi = useActions('callApi')

  let scheduler = data?.scheduler || {}
  let enabled = !!scheduler.enabled

  let events = scheduler.events || []
  let enable = eventName => callApi('scheduler/enableEvent', {eventName}, reload)
  let disable = eventName => callApi('scheduler/disableEvent', {eventName}, reload)

  const columns = [
    {
      Header: 'Source',
      accessor: 'eventSource',
      width: 70,
    },
    {
      Header: 'Name',
      accessor: 'eventName',
    },
    {
      Header: 'Job',
      accessor: 'eventJob',
    },
    {
      Header: 'When',
      id: 'when',
      accessor: () => null,
      Cell: ({original}) => {
        let {eventTime, eventWeekday, eventInterval, eventSpreadMinutes} = original
        if (eventInterval) {
          return `each ${eventInterval}s`
        }
        let day = eventWeekday || 'day'
        let time = eventTime
        let spread = eventSpreadMinutes ? `~${eventSpreadMinutes}min` : ''
        return `each ${day}, ${time}${spread}`
      },
    },
    {
      Header: 'Domain',
      accessor: 'eventDomain',
      Cell: ({value}) => <DomainLabel dname={value} />,
    },
    {
      Header: 'On',
      accessor: 'eventActive',
      Cell: ({value, original}) => (
        <Button
          fluid
          color={value ? 'green' : 'grey'}
          onClick={() => (value ? disable(original.eventName) : enable(original.eventName))}
        >
          {String(value)}
        </Button>
      ),
      width: 80,
    },
  ]

  let menu = (
    <Menu>
      <Menu.Item
        icon={{name: 'cogs', color: enabled ? 'green' : 'grey'}}
        content={enabled ? 'Aktiv' : 'Inaktiv'}
      />
    </Menu>
  )
  return (
    <TableSegment icon={jobsIcon('calendar outline')} header="Scheduler" menu={menu}>
      <TranslatedTable
        flex
        className="-striped -highlight dynHeight"
        data={events}
        columns={columns}
        showPagination={false}
        pageSize={Math.max(10, events.length)}
        minRows={0}
      />
    </TableSegment>
  )
}
